import { ArrivalNotice } from '@models/next/ArrivalNotice';
import { BookingInquiryFields } from '@models/next/BookingInquiry';
import { CompanyFields } from '@models/next/Company';
import { CustomsInstructionFields } from '@models/next/CustomsInstruction';
import { CustomsRequest } from '@models/next/CustomsRequest';
import { DeliveryInstruction } from '@models/next/DeliveryInstruction';
import { DeliveryRequest } from '@models/next/DeliveryRequest';
import { DeliveryRequestPickupAddress } from '@models/next/DeliveryRequestPickupAddress';
import { InvoiceDetails, InvoiceFields } from '@models/next/Invoice';
import { PaymentSlip } from '@models/next/PaymentSlip';
import { PreAlertFields } from '@models/next/PreAlert';
import { PriceSimulatorForm } from '@models/next/PriceSimulator';
import { RoutePlanSimulator } from '@models/next/RoutePlanSimulator';
import { AdminUser, User } from '@models/next/User';
import { z } from 'zod';

export const PostPreviewCustomsRequest = CustomsRequest.pick({
  cargo_move_in_date: true,
  free_time: true,
  tentative_pickup_date: true,
  container_no: true,
  submission_target: true,
  owner_company: true,
  special_note: true,
  memo: true,
}).extend({
  booking_inquiry: BookingInquiryFields.pick({
    d2d_no: true,
    use_rcep: true,
    trade_condition: true,
    receiver: true,
    consignee: true,
    salesperson_japan: true,
    owner: true,
    owner_company: true,
  }),
  delivery_request: z
    .object({
      truck_type: z.string(),
    })
    .nullish(),
  arrival_notice: DeliveryRequestPickupAddress,
  pre_alert: PreAlertFields.pick({
    job_code: true,
    vessel: true,
    voyage: true,
    pol: true,
    pod: true,
    etd: true,
    eta: true,
    ata: true,
    house_bl_number: true,
    commodity: true,
    quantity: true,
    packages: true,
    gross_weight: true,
    measurement: true,
  }),
  customs_instruction: CustomsInstructionFields.pick({
    insurance: true,
  }),
  creator: AdminUser,
});

export const PostPreviewDeliveryRequest = DeliveryRequest.pick({
  delivery_request_no: true,
  pickup_date: true,
  pickup_address: true,
  delivery_date_type: true,
  delivery_date: true,
  truck_type: true,
  truck_count: true,
  memo: true,
  do_less_no: true,
  move_out_no: true,
  partner: true,
  delivery_info: true,
}).extend({
  created_at: z.string(),
  updated_at: z.string(),
  delivery_instruction: DeliveryInstruction.pick({
    is_receiver_checked: true,
    receiver: true,
    special_note: true,
    has_special_note: true,
  }),
  booking_inquiry: BookingInquiryFields.pick({
    d2d_no: true,
    consignee: true,
    receiver: true,
  }),
  pre_alert: PreAlertFields.pick({
    job_code: true,
    vessel: true,
    voyage: true,
    pol: true,
    pod: true,
    etd: true,
    eta: true,
    ata: true,
    house_bl_number: true,
    commodity: true,
    quantity: true,
    packages: true,
    gross_weight: true,
    measurement: true,
  }),
});

export type PostPreviewDeliveryRequest = z.infer<typeof PostPreviewDeliveryRequest>;

export const PostPreviewPaymentSlip = PaymentSlip.pick({
  invoice_no: true,
  invoice_date: true,
  memo: true,
  invoice_file: true,
  payment_slip_no: true,
  payment_type: true,
  deadline: true,
  payment_date: true,
  conversion_rate: true,
  conversion_rate_date: true,
  payment_details: true,
  payee: true,
}).extend({
  pre_alert: PreAlertFields.pick({
    pol: true,
    pod: true,
    quantity: true,
    packages: true,
    vessel: true,
    voyage: true,
    ata: true,
  }),
  booking_inquiry: BookingInquiryFields.pick({
    d2d_no: true,
    consignee: true,
    receiver: true,
  }),
  customs_request: CustomsRequest.pick({ check_type: true }).nullish(),
});

export type PostPreviewPaymentSlip = z.infer<typeof PostPreviewPaymentSlip>;

export const PostPreviewArrivalNotice = ArrivalNotice.pick({
  delivery_destination: true,
  repository: true,
  address: true,
  tel: true,
  fax: true,
  principal_name: true,
  house_bl_number: true,
  shipper: true,
  consignee: true,
  shipping_mark: true,
  vessel: true,
  voyage: true,
  pod: true,
  pol: true,
  eta: true,
  etd: true,
  commodity: true,
  nos: true,
  packages: true,
  gross_weight: true,
  measurement: true,
  overseas_local_amount: true,
  d2d_taxable_amount: true,
  payments_terms: true,
  exchange_rate: true,
});

export type PostPreviewArrivalNotice = z.infer<typeof PostPreviewArrivalNotice>;

export const PostPreviewInvoice = InvoiceFields.pick({
  house_bl_number: true,
  charge_date: true,
  client_name: true,
  client_name_en: true,
  client_zipcode: true,
  client_address1: true,
  client_address2: true,

  job_code: true,
  charge_pay_day: true,

  vessel_voyage: true,
  transport_route: true,
  ata: true,
  quantity_packages: true,

  d2d_charge_no: true,
  customer_no: true,
  conversion_rate: true,
  language: true,
  status: true,
}).extend({
  invoice_details: InvoiceDetails.array(),
});

export type PostPreviewInvoice = z.infer<typeof PostPreviewInvoice>;

export const PostPreviewClientPriceSimulator = z.object({
  user: User,
  company: CompanyFields.pick({
    company_name: true,
  }),
  route_plan_simulators: RoutePlanSimulator.array(),
  price_simulator_form: PriceSimulatorForm,
});

export type PostPreviewClientPriceSimulator = z.infer<typeof PostPreviewClientPriceSimulator>;
